/**
 * Polls async bot QA analysis job status on reporting-service.
 */

import { botReportingQaApiClient } from "@/api/BotReportingQaApiClient";

const DEFAULT_INTERVAL_MS = 2000;
const DEFAULT_TIMEOUT_MS = 300000;

export class BotQaJobPoller {
  constructor(apiClient = botReportingQaApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * @param {string} jobId
   * @param {{
   *   signal?: AbortSignal,
   *   intervalMs?: number,
   *   timeoutMs?: number,
   *   onStatus?: (status: string) => void,
   * }} [opts]
   */
  async pollUntilComplete(jobId, opts = {}) {
    const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
    const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const started = Date.now();

    while (Date.now() - started < timeoutMs) {
      if (opts.signal?.aborted) {
        throw new Error("Analysis cancelled");
      }

      let row;
      try {
        row = await this.apiClient.getQaJobStatus(jobId, { signal: opts.signal });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg.includes("404") || msg.toLowerCase().includes("not found")) {
          throw new Error("Analysis job expired or was not found. Please run again.");
        }
        throw e;
      }

      const status = String(row?.status ?? "unknown");
      opts.onStatus?.(status);

      if (status === "completed") {
        return row?.result ?? { status: "success" };
      }
      if (status === "failed") {
        const errMsg = row?.error ? String(row.error) : "Analysis failed";
        throw new Error(errMsg);
      }

      await new Promise((resolve, reject) => {
        const t = setTimeout(resolve, intervalMs);
        opts.signal?.addEventListener(
          "abort",
          () => {
            clearTimeout(t);
            reject(new Error("Analysis cancelled"));
          },
          { once: true },
        );
      });
    }

    throw new Error("Analysis timed out. Try again or reduce the date range.");
  }
}

export const botQaJobPoller = new BotQaJobPoller();
